/** @jsxImportSource @emotion/react */
import { useEffect, useState } from 'react'
import { IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@material-ui/core'
import EditIcon from '@material-ui/icons/Edit'
import DeleteIcon from '@material-ui/icons/Delete'

import { Loader, LoaderType } from 'components/widgets'
import { TypeToConfirmDialog } from 'components/partials'

import { getGroups } from 'models/api/orgs'
import { deleteOrg as apiDeleteOrg } from 'models/api/orgs'
import * as NavState from 'models/NavState'

import { Organization, trans, TranslationGroup, TranslationKey, ucfirst } from 'lib/types'

import { useGlobal } from 'contexts/GlobalProvider'
import { useAdmin } from 'contexts/AdminProvider'

// Row-level hover behavior: actions are invisible until the row is hovered.
const rowCss = {
  '& .rm-row-actions': { visibility: 'hidden' as const },
  '&:hover .rm-row-actions': { visibility: 'visible' as const },
}

export const GroupsTab = () => {
  const orgsTranslation: TranslationGroup = trans.merge(TranslationKey.ORGS)
  const adminTranslation: TranslationGroup = trans.merge(TranslationKey.ADMIN)
  const common: TranslationGroup = trans.common()

  const { setSelectedOrg, setSelectedOrgId } = useGlobal()
  const { setSelectedAdminOrgId } = useAdmin()

  const [groups, setGroups] = useState<Organization[]>([])
  const [loading, setLoading] = useState(true)
  const [pendingDelete, setPendingDelete] = useState<Organization | null>(null)

  const load = () => {
    setLoading(true)
    getGroups()
      .then(setGroups)
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
  }, [])

  const handleEdit = (org: Organization) => {
    NavState.setSelectedAdminOrgId(org.id)
    setSelectedAdminOrgId(org.id)
    setSelectedOrg(org)
    setSelectedOrgId(org.id)
  }

  const handleConfirmDelete = () => {
    if (!pendingDelete) return
    const id = pendingDelete.id
    setPendingDelete(null)
    apiDeleteOrg(id).then(load)
  }

  if (loading) {
    return (
      <div css={{ padding: '40px 0', display: 'flex', justifyContent: 'center' }}>
        <Loader type={LoaderType.ROTATING_LINES} width="48" />
      </div>
    )
  }

  return (
    <>
      <TableContainer css={{ maxWidth: 480 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell css={{ fontWeight: 600 }}>{common.Name}</TableCell>
              <TableCell align="right" css={{ width: 120 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.length === 0 ? (
              <TableRow>
                <TableCell colSpan={2} css={{ textAlign: 'center', opacity: 0.7 }}>
                  {adminTranslation.no_groups || 'No groups'}
                </TableCell>
              </TableRow>
            ) : (
              groups.map(group => (
                <TableRow key={group.id} css={rowCss} hover>
                  <TableCell>{group.name}</TableCell>
                  <TableCell align="right">
                    <div className="rm-row-actions">
                      <IconButton size="small" onClick={() => handleEdit(group)} aria-label={common.edit || 'Edit'}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => setPendingDelete(group)}
                        aria-label={common.delete || 'Delete'}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* The trash icon lives in a hover-revealed row action one row away from every
          other group, so the confirmation makes the operator reproduce this group's
          own name before it will arm. */}
      <TypeToConfirmDialog
        open={!!pendingDelete}
        title={`${ucfirst(orgsTranslation.delete)} ${orgsTranslation.group}`}
        description={
          <>
            <div>
              {orgsTranslation.confirm_delete} <strong>{pendingDelete?.name || ''}</strong>?
            </div>
            <div style={{ marginTop: '8px' }}>{orgsTranslation.delete_is_permanent}</div>
          </>
        }
        confirmPhrase={pendingDelete?.name || ''}
        confirmLabel={ucfirst(orgsTranslation.delete)}
        onConfirm={handleConfirmDelete}
        handleClose={() => setPendingDelete(null)}
      />
    </>
  )
}

export default GroupsTab
